import { useFormik } from "formik";
import { mutate } from "swr";
import { useNavigate, useParams } from "react-router-dom";

import endpoints from "../../api/endpoints";
import apiInstance from "../../api/instance";
import useGetApi from "../../hooks/use-get-api";
import parseNumber from "../../utils/parse-number";
import { editUserCommissionSchema } from "../../utils/validation-schema";
import { showErrorToast, showSuccessToast } from "../../utils/toast";

const COMMISSION_MAX = 5;

type EditableMember = {
  user_id?: string;
  admin_id?: string;
  user_name?: string;
  display_name?: string;
  role_name?: string;
  created_by?: string;
  commission?: number;
  fancy_comm?: number;
  bookmarker_comm?: number;
  bookmaker_comm?: number;
  commission_hierarchy?: Array<Record<string, unknown>>;
  parent_commission?: {
    commission?: number;
    fancy_comm?: number;
    bookmarker_comm?: number;
    bookmaker_comm?: number;
  };
};

const round2 = (n: number) => Number(n.toFixed(2));

export default function EditAccount() {
  const { userId = "" } = useParams();
  const navigate = useNavigate();
  const { data: member, isLoading } = useGetApi<EditableMember>(endpoints.member.getMember(userId), !!userId);

  const num = (...vals: unknown[]) => {
    for (const v of vals) {
      if (v === undefined || v === null || v === "") continue;
      const n = Number(v);
      if (Number.isFinite(n)) return n;
    }
    return 0;
  };
  const parentFloors = {
    commission: num(member?.parent_commission?.commission),
    bookmarker_comm: num(
      member?.parent_commission?.bookmarker_comm,
      member?.parent_commission?.bookmaker_comm
    ),
    fancy_comm: num(member?.parent_commission?.fancy_comm),
  };

  const memberTotals = {
    commission: Number(member?.commission ?? parentFloors.commission),
    bookmarker_comm: Number(member?.bookmarker_comm ?? member?.bookmaker_comm ?? parentFloors.bookmarker_comm),
    fancy_comm: Number(member?.fancy_comm ?? parentFloors.fancy_comm),
  };

  // Form stores "Our" = absolute total - upline. Same for admins and users on template 7.
  const initialOur = {
    commission: round2(Math.max(memberTotals.commission - parentFloors.commission, 0)),
    bookmarker_comm: round2(Math.max(memberTotals.bookmarker_comm - parentFloors.bookmarker_comm, 0)),
    fancy_comm: round2(Math.max(memberTotals.fancy_comm - parentFloors.fancy_comm, 0)),
  };

  const { values, touched, errors, handleChange, handleBlur, handleSubmit, isSubmitting, setSubmitting } = useFormik({
    enableReinitialize: true,
    initialValues: {
      commission: initialOur.commission,
      bookmarker_comm: initialOur.bookmarker_comm,
      fancy_comm: initialOur.fancy_comm,
      transaction_password: "",
    },
    validationSchema: editUserCommissionSchema,
    onSubmit: async (formValues) => {
      const ourBetfair = parseNumber({ type: "float", value: formValues.commission });
      const ourBookmaker = parseNumber({ type: "float", value: formValues.bookmarker_comm });
      const ourSession = parseNumber({ type: "float", value: formValues.fancy_comm });
      const betfairTotal = round2(parentFloors.commission + ourBetfair);
      const bookmakerTotal = round2(parentFloors.bookmarker_comm + ourBookmaker);
      const sessionTotal = round2(parentFloors.fancy_comm + ourSession);
      if (betfairTotal > COMMISSION_MAX || bookmakerTotal > COMMISSION_MAX || sessionTotal > COMMISSION_MAX) {
        showErrorToast(`Total commission cannot exceed ${COMMISSION_MAX}%`);
        setSubmitting(false);
        return;
      }
      try {
        await apiInstance.put(endpoints.ACCOUNT.updateUser(userId), {
          commission: betfairTotal,
          fancy_comm: sessionTotal,
          bookmarker_comm: bookmakerTotal,
          bookmaker_comm: bookmakerTotal,
          password: formValues.transaction_password,
        });
        showSuccessToast("Commission updated successfully");
        await mutate(endpoints.member.getMember(userId));
        if (member?.created_by) {
          navigate(`/active-users/${member.created_by}`);
        } else {
          navigate(-1);
        }
      } catch (error: any) {
        showErrorToast(error?.response?.data?.status_message ?? "Something Went Wrong");
        setSubmitting(false);
      }
    },
  });

  return (
    <div className="page-content">
      <div className="add-account">
        <h2 className="m-b-20">Edit Account</h2>
        {isLoading && <p>Loading account...</p>}
        {!isLoading && !member && <p>Account not found.</p>}
        {!isLoading && member && (
          <form method="post" onSubmit={handleSubmit}>
            <div className="row">
              <div className="col-6">
                <div className="form-group">
                  <label htmlFor="user_name">Client Name:</label>
                  <input
                    id="user_name"
                    type="text"
                    className="form-control"
                    value={member?.user_name ?? ""}
                    readOnly
                  />
                </div>
              </div>
              <div className="col-6">
                <div className="form-group">
                  <label htmlFor="display_name">Full Name:</label>
                  <input
                    id="display_name"
                    type="text"
                    className="form-control"
                    value={member?.display_name ?? ""}
                    readOnly
                  />
                </div>
              </div>
            </div>

            <div className="row mt-4">
              <div className="col-12">
                <h4 className="mb-4">Commission Settings</h4>
              </div>
              {(
                [
                  {
                    key: "commission",
                    label: "Betfair",
                    upline: parentFloors.commission,
                    our: Number(values.commission),
                    error: errors.commission,
                    touched: touched.commission,
                  },
                  {
                    key: "bookmarker_comm",
                    label: "Bookmaker",
                    upline: parentFloors.bookmarker_comm,
                    our: Number(values.bookmarker_comm),
                    error: errors.bookmarker_comm,
                    touched: touched.bookmarker_comm,
                  },
                  {
                    key: "fancy_comm",
                    label: "Session",
                    upline: parentFloors.fancy_comm,
                    our: Number(values.fancy_comm),
                    error: errors.fancy_comm,
                    touched: touched.fancy_comm,
                  },
                ] as const
              ).map((field) => {
                const ourMax = round2(COMMISSION_MAX - field.upline);
                const total = round2(field.upline + (Number.isFinite(field.our) ? field.our : 0));
                return (
                  <div className="col-md-4" key={field.key}>
                    <h5 className="mb-2">{field.label}</h5>
                    <table className="table table-striped table-bordered">
                      <tbody>
                        <tr>
                          <td>Upline</td>
                          <td>{field.upline}</td>
                        </tr>
                        <tr>
                          <td>Our</td>
                          <td>
                            <input
                              type="number"
                              min={0}
                              max={Math.max(ourMax, 0)}
                              step={0.01}
                              name={field.key}
                              className={`form-control${field.touched && field.error ? " is-invalid" : ""}`}
                              value={field.our}
                              onChange={handleChange}
                              onBlur={handleBlur}
                            />
                            <small className="text-muted">{`0% to ${Math.max(ourMax, 0)}%`}</small>
                            {field.touched && field.error && (
                              <small className="error d-block">{field.error}</small>
                            )}
                          </td>
                        </tr>
                        <tr>
                          <td>Total</td>
                          <td>{Number.isFinite(total) ? total : field.upline}</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                );
              })}
            </div>

            <>
                <div className="row mt-4">
                  <div className="col-12">
                    <div className="form-group col-3 float-right">
                      <label htmlFor="transaction_password">Transaction Password:</label>
                      <input
                        id="transaction_password"
                        placeholder="Transaction Password"
                        type="password"
                        name="transaction_password"
                        className={`form-control${touched.transaction_password && errors.transaction_password ? " is-invalid" : ""}`}
                        value={values.transaction_password}
                        onChange={handleChange}
                        onBlur={handleBlur}
                      />
                      {touched.transaction_password && errors.transaction_password && (
                        <small className="error">{errors.transaction_password}</small>
                      )}
                    </div>
                  </div>
                </div>

                <div className="row m-t-20">
                  <div className="col-md-12">
                    <div className="float-right">
                      <button type="submit" className="btn btn-submit" disabled={isSubmitting}>
                        Update Account
                      </button>
                    </div>
                  </div>
                </div>
            </>
          </form>
        )}
      </div>
    </div>
  );
}
